import { DocumentItemTypeEnum } from 'src/app/domain/enums/documents/document-item-type.enum';

/** Pedaço da miniatura: cada um vira um desenho simplificado na página. */
export type ThumbnailBlock =
    | { kind: 'heading' }
    | { kind: 'text'; widths: number[] }
    | { kind: 'list'; widths: number[] }
    | { kind: 'callout' }
    | { kind: 'table'; rows: number; cols: number }
    | { kind: 'stats'; count: number }
    | { kind: 'semaphore'; tones: ThumbnailTone[] }
    | { kind: 'photos' }
    | { kind: 'signature' }
    | { kind: 'total' };

export type ThumbnailTone = 'ok' | 'warn' | 'bad' | 'none';

/** Só o que a miniatura lê de uma seção (serve para modelo interno e da empresa). */
export interface ThumbnailSection {
    items?: { type?: string; value?: unknown }[];
}

/**
 * A página da miniatura tem altura de A4 e corta embaixo, como a prévia do Word: passar
 * disso só gasta DOM sem aparecer.
 */
export const THUMBNAIL_MAX_BLOCKS = 16;

/** Larguras das linhas de texto, em %, num padrão que parece texto corrido. */
const LINE_WIDTHS = [96, 88, 92, 71, 94, 83, 64];

/**
 * Desenha a estrutura de um modelo a partir das seções dele: título de cada seção,
 * parágrafos (com quantas linhas o texto ocupa), listas, tabelas, destaques, indicadores,
 * semáforo, fotos e assinatura. Orçamento ganha no topo a tabela de itens e o total, que
 * no documento vêm da configuração do orçamento e não das seções.
 */
export function buildThumbnailBlocks(
    sections: ThumbnailSection[] | undefined,
    options: { quote?: boolean } = {}
): ThumbnailBlock[] {
    const blocks: ThumbnailBlock[] = [];
    let line = 0;
    const nextWidths = (count: number): number[] =>
        Array.from({ length: count }, () => LINE_WIDTHS[line++ % LINE_WIDTHS.length]);

    if (options.quote) {
        blocks.push({ kind: 'table', rows: 4, cols: 4 }, { kind: 'total' });
    }

    for (const section of sections ?? []) {
        for (const item of section.items ?? []) {
            const block = toBlock(item, nextWidths);
            if (block) blocks.push(block);
            if (blocks.length >= THUMBNAIL_MAX_BLOCKS) return blocks;
        }
    }

    return blocks;
}

function toBlock(
    item: { type?: string; value?: unknown },
    nextWidths: (count: number) => number[]
): ThumbnailBlock | null {
    switch (item.type) {
        case DocumentItemTypeEnum.TITLE:
            return { kind: 'heading' };

        case DocumentItemTypeEnum.TEXT:
        case DocumentItemTypeEnum.SUBTITLE: {
            const { text, listItems } = readDelta(item.value);
            if (listItems > 0) return { kind: 'list', widths: nextWidths(Math.min(listItems, 4)) };
            if (!text.trim()) return null;
            // ~70 caracteres por linha na largura da página do PDF.
            return { kind: 'text', widths: nextWidths(clamp(Math.ceil(text.length / 70), 1, 4)) };
        }

        case DocumentItemTypeEnum.CALLOUT:
            return { kind: 'callout' };

        case DocumentItemTypeEnum.TABLE: {
            const value = item.value as { columns?: unknown[]; rows?: unknown[] } | undefined;
            return {
                kind: 'table',
                rows: clamp((value?.rows?.length ?? 2) + 1, 2, 5),
                cols: clamp(value?.columns?.length ?? 3, 2, 4),
            };
        }

        case DocumentItemTypeEnum.STATS: {
            const cards = Array.isArray(item.value) ? item.value.length : 3;
            return { kind: 'stats', count: clamp(cards, 2, 4) };
        }

        case DocumentItemTypeEnum.SEMAPHORE: {
            const entries = (item.value as { items?: { status?: string }[] } | undefined)?.items ?? [];
            const tones = entries.slice(0, 4).map(entry => toTone(entry.status));
            return { kind: 'semaphore', tones: tones.length ? tones : ['ok', 'warn', 'bad'] };
        }

        case DocumentItemTypeEnum.IMAGE:
        case DocumentItemTypeEnum.GROUP_IMAGE:
        case DocumentItemTypeEnum.VIDEO:
        case DocumentItemTypeEnum.GROUP_VIDEO:
            return { kind: 'photos' };

        case DocumentItemTypeEnum.SIGNATURE:
            return { kind: 'signature' };

        default:
            return null;
    }
}

/** Texto corrido e quantidade de itens de lista de um Delta (string JSON ou objeto). */
function readDelta(value: unknown): { text: string; listItems: number } {
    let ops: { insert?: unknown; attributes?: { list?: string } }[] = [];

    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        ops = Array.isArray(parsed?.ops) ? parsed.ops : [];
    } catch {
        // Texto puro (legado): conta como parágrafo.
        return { text: typeof value === 'string' ? value : '', listItems: 0 };
    }

    let text = '';
    let listItems = 0;
    for (const op of ops) {
        if (typeof op.insert === 'string') text += op.insert;
        else if (op.insert) text += '        '; // tag de dados ocupa espaço de uma palavra
        if (op.attributes?.list) listItems++;
    }

    return { text, listItems };
}

function toTone(status: string | undefined): ThumbnailTone {
    return status === 'ok' || status === 'warn' || status === 'bad' ? status : 'none';
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}
